import { sortBy } from 'lodash';
import { Control, Controller, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form/dist/types/errors';

import { QueryResponse as IBoardInitResponse } from 'api/queries/getBoardInitLite.query';
import { QueryResponse as CardResponse } from 'api/queries/getMainCard.query';

import { AssigneeAvatarWithInlineSelect } from 'components/common/AssigneeAvatarWithInlineSelect/AssigneeAvatarWithInlineSelect';
import { IFields } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter';
import SelectWithCreate from 'components/common/SelectWithCreate/SelectWithCreate';
import { useSelectWithCreateHook } from 'components/common/SelectWithCreate/SelectWithCreateHook';
import RichTextArea from 'components/common/richTextArea/richTextArea';

import { AssociatedDocuments } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/AssociatedDocuments/AssociatedDocuments';
import { CardPillsAndIndicators } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/CardPillsAndIndicators/CardPillsAndIndicators';
import Comments from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/comments/comments';
import { IAssigneeWithExtraData } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/CreateCardModal.types';
import { userMentionMapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/MainCard.utils';
import { ProgramActivityRelatedActivityDates } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/components/CardContent/CardDetails/ProgramActivityRelatedActivityDates/ProgramActivityRelatedActivityDates';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/components/CardContent/CardDetails/cardDetails.module.scss';
import { Switch } from 'components/DesignSystem/Switch';
import { CustomizedTooltip } from 'components/common';

interface Props {
    setValue: UseFormSetValue<IFields>;
    control: Control<IFields, IFields, IFields>;
    existingDetailsData: string | undefined;
    boardInit: IBoardInitResponse | undefined;
    handleDetails: (editorState, mentionedList) => void; // FIX TYPES OF THE ARGUMENTS
    data: CardResponse;
    queryKeys: string[];
    register: UseFormRegister<IFields>;
    errors: FieldErrors<IFields>;
    tagsOptions: { label: string; value: string }[];
    watch: UseFormWatch<IFields>;
    submittedDurationComplete: string | number;
    assigneeValue: IAssigneeWithExtraData | null;
    handleAssigneeSelect: (value) => void;
}

export const CardDetailsDesktop = ({
    data,
    watch,
    submittedDurationComplete,
    handleDetails,
    existingDetailsData,
    boardInit,
    queryKeys,
    tagsOptions,
    control,
    setValue,
    register,
    errors,
    assigneeValue,
    handleAssigneeSelect,
}: Props) => {
    const { inputValue, onInputChange, onChange } = useSelectWithCreateHook({
        onChangeCallback: (value) =>
            setValue(
                'tagList',
                value.map((item) => ({ label: item.value.trim(), value: item.value.trim() })),
                { shouldDirty: true },
            ),
    });
    return (
        <div className={classes.cardDetailsContainer}>
            <div className={classes.leftSection}>
                <CardPillsAndIndicators
                    data={data}
                    watch={watch}
                    submittedDurationComplete={submittedDurationComplete}
                />

                <div data-track-id={`text-editor-card`}>
                    <div className={classes.title}>Details:</div>
                    <RichTextArea
                        onEditorDescription={handleDetails}
                        existingData={existingDetailsData}
                        mention={{
                            separator: ' ',
                            trigger: '@',
                            suggestions: sortBy(boardInit?.assignee.map(userMentionMapper), 'text') || [],
                        }}
                        id={'cardModalDetails'}
                        placeholder={
                            'Enter here activity narrative, mitigation, or risk details. \nType @ to mention another user and notify them about it.'
                        }
                        editorTextClassName={classes.editorTextArea}
                    />
                </div>

                <Comments
                    id={data.id}
                    isVirtual={data?.isVirtual}
                    card={data}
                    assigneeOptions={sortBy(boardInit?.assignee.map(userMentionMapper), 'text') || []}
                    queryKeys={queryKeys}
                />
            </div>

            <div className={classes.rightSection}>
                <div className={classes.selectWrapper} data-track-id={`tags-select-card`}>
                    <div className={classes.title}>Tags:</div>
                    <div className={classes.content}>
                        <Controller
                            render={({ field }) => (
                                <SelectWithCreate
                                    {...field}
                                    options={tagsOptions}
                                    id={'cardModalTags'}
                                    isMulti={true}
                                    isClearable={true}
                                    inputValue={inputValue}
                                    onInputChange={onInputChange}
                                    onChange={onChange}
                                />
                            )}
                            name={`tagList`}
                            control={control}
                        />
                    </div>
                </div>

                <div className={classes.selectWrapper}>
                    <div className={classes.title}>Assignee *:</div>
                    <div className={classes.content}>
                        <div className={classes.inlineSelectWrapper}>
                            <Controller
                                render={({ field }) => (
                                    <AssigneeAvatarWithInlineSelect
                                        {...field}
                                        assigneeValue={assigneeValue}
                                        onSelection={handleAssigneeSelect}
                                    />
                                )}
                                name={`assignee`}
                                control={control}
                                rules={{
                                    required: 'Assignee is Required',
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className={classes.selectWrapper}>
                    <div className={classes.title}>Important:</div>
                    <div className={classes.content}>
                        <CustomizedTooltip
                            triggerElement={
                                <Controller
                                    render={({ field }) => <Switch checked={field.value} onChange={field.onChange} />}
                                    name={`isImportant`}
                                    control={control}
                                />
                            }
                            tooltipContent={'This setting effects Priority Matrix'}
                        />
                    </div>
                </div>

                <div className={classes.selectWrapper}>
                    <div className={classes.title}>Category *:</div>
                    <div data-testid={'category-content'} className={classes.content}>
                        {watch('category')?.label}
                    </div>
                </div>

                <ProgramActivityRelatedActivityDates
                    data={data}
                    setValue={setValue}
                    control={control}
                    watch={watch}
                    register={register}
                    errors={errors}
                />
                <AssociatedDocuments watch={watch} setValue={setValue} />
            </div>
        </div>
    );
};
